import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Lock, Trophy, ArrowRight, CheckCircle2, XCircle, Clock, ShieldCheck, TrendingUp, Star } from 'lucide-react';
import Seo from '../components/Seo';
import { fetchDailyTips } from '../api';

const outcome = (result) => {
  const r = (result || '').toLowerCase();
  if (r.includes('won') || r.includes('win')) return 'won';
  if (r.includes('lost') || r.includes('loss')) return 'lost';
  return 'pending';
};

const markets = [
  { t: '1 / X / 2', d: 'Home win, draw or away win — the classic match result market.' },
  { t: 'Over/Under 2.5 Goals', d: 'Whether the total goals scored in a match will be above or below 2.5.' },
  { t: 'BTTS (Both Teams To Score)', d: 'A bet on whether both teams find the net during the match.' },
  { t: 'Double Chance', d: 'Covers two of the three possible outcomes (1X, 12 or X2) for lower risk.' },
];

function FeaturedTip({ tip }) {
  const locked = tip.tier === 'vip';
  const o = outcome(tip.result);
  return (
    <section className="bg-[#15120f] pt-12" data-testid="tip-of-the-day">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-orange-500/40 bg-gradient-to-br from-[#241a10] via-[#1f1913] to-[#15120f] p-6 shadow-2xl shadow-black/50 sm:p-9">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-3 py-1 font-heading text-xs font-extrabold uppercase tracking-wide text-[#15120f]">
                <Star className="h-3.5 w-3.5" /> Tip Of The Day
              </span>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-neutral-400">
                {tip.league && <span className="rounded-full bg-white/5 px-3 py-1 font-semibold text-neutral-300">{tip.league}</span>}
                {tip.date && <span>{tip.date}</span>}
                {tip.kickoff && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {tip.kickoff}</span>}
              </div>
              <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight text-white sm:text-4xl">{tip.match}</h2>
              {!locked && tip.analysis && <p className="mt-3 text-sm leading-relaxed text-neutral-300 sm:text-base">{tip.analysis}</p>}
              {!locked && o !== 'pending' && (
                <div className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase ${o === 'won' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                  {o === 'won' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {tip.result}
                </div>
              )}
            </div>

            <div className="w-full flex-shrink-0 lg:w-80">
              {locked ? (
                <div className="flex flex-col items-center rounded-2xl border border-orange-500/30 bg-[#15120f]/70 p-6 text-center backdrop-blur">
                  <Lock className="h-8 w-8 text-orange-500" />
                  <p className="mt-3 font-heading text-lg font-bold text-white">Premium VIP Pick</p>
                  <p className="mt-1 text-sm text-neutral-400">Unlock today's featured selection with a subscription.</p>
                  <Link to="/pricing" className="btn-orange mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg py-3 font-heading text-sm font-bold uppercase tracking-wide">Unlock Now <ArrowRight className="h-4 w-4" /></Link>
                </div>
              ) : (
                <div className="rounded-2xl border border-[#2e2822] bg-[#15120f]/70 p-6 backdrop-blur">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-neutral-500">Our Tip</p>
                      <p className="font-heading text-3xl font-extrabold text-orange-400">{tip.tip}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wide text-neutral-500">Odd</p>
                      <p className="font-heading text-3xl font-extrabold text-white">{tip.odd}</p>
                    </div>
                  </div>
                  <Link to="/pricing" className="btn-orange mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg py-3 font-heading text-sm font-bold uppercase tracking-wide"><Trophy className="h-4 w-4" /> Get More Tips</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TipCard({ tip, locked }) {
  const o = outcome(tip.result);
  return (
    <div className={`card-dark relative flex flex-col overflow-hidden rounded-2xl p-6 ${locked ? '' : 'ring-1 ring-orange-500/20'}`} data-testid="daily-tip-card">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-orange-400">{tip.league || 'Football'}</span>
        {tip.tier === 'vip'
          ? <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-extrabold uppercase text-[#15120f]"><Lock className="h-3 w-3" /> VIP</span>
          : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-extrabold uppercase text-emerald-400">Free</span>}
      </div>

      <h3 className="mt-4 font-heading text-xl font-extrabold text-white">{tip.match}</h3>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
        {tip.date && <span>{tip.date}</span>}
        {tip.kickoff && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {tip.kickoff}</span>}
      </div>

      <div className={`mt-5 rounded-xl border border-[#2e2822] bg-[#15120f] p-4 ${locked ? 'relative' : ''}`}>
        {locked ? (
          <div className="flex flex-col items-center py-2 text-center">
            <Lock className="h-6 w-6 text-orange-500" />
            <p className="mt-2 text-sm font-semibold text-neutral-300">VIP pick — unlocked for members</p>
            <Link to="/pricing" className="btn-orange mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-wide">Unlock <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-neutral-500">Our Tip</p>
              <p className="font-heading text-2xl font-extrabold text-orange-400">{tip.tip}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-neutral-500">Odd</p>
              <p className="font-heading text-2xl font-extrabold text-white">{tip.odd}</p>
            </div>
          </div>
        )}
      </div>

      {!locked && tip.analysis && (
        <p className="mt-4 text-sm leading-relaxed text-neutral-400">{tip.analysis}</p>
      )}

      {!locked && o !== 'pending' && (
        <div className={`mt-4 inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-xs font-bold uppercase ${o === 'won' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
          {o === 'won' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {tip.result}
        </div>
      )}
    </div>
  );
}

export default function PredictionsToday() {
  const [tips, setTips] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try { setTips(await fetchDailyTips()); } catch (e) { /* empty */ }
      finally { setLoaded(true); }
    })();
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Football Predictions Today',
    description: 'Free daily football predictions and expert betting tips, updated every day. See our free picks and unlock premium VIP tips.',
    url: 'https://24betbox.com/football-predictions-today',
    publisher: { '@type': 'Organization', name: '24BetBox' },
  };

  const freeTips = tips.filter((t) => t.tier !== 'vip');
  const featured = freeTips[0] || tips[0] || null;
  const rest = featured ? tips.filter((t) => t.id !== featured.id) : tips;

  return (
    <div data-testid="predictions-today-page">
      <Seo
        title="Football Predictions Today | Free Daily Betting Tips – 24BetBox"
        description="Today's football predictions and free betting tips from verified tipsters. Get daily 1X2, Over 2.5 goals and BTTS picks, plus premium VIP football tips."
        path="/football-predictions-today"
        type="website"
        jsonLd={jsonLd}
      />

      <section className="hero-gradient border-b border-[#2e2822]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-orange-400">
            <Flame className="h-3.5 w-3.5" /> Updated Daily
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">Football Predictions Today</h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">
            Fresh daily football predictions from our team of verified professional tipsters. Browse today's free picks — including 1X2, Over 2.5 goals and BTTS tips — and unlock our premium VIP selections for the biggest edge.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-neutral-400">
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Free daily tips</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Transparent results</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Expert analysis</span>
          </div>
        </div>
      </section>

      {loaded && featured && <FeaturedTip tip={featured} />}

      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Today's Selections</p>
              <h2 className="mt-2 font-heading text-3xl font-extrabold text-white sm:text-4xl">Latest Football Tips</h2>
            </div>
            <Link to="/pricing" className="btn-orange inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-heading text-sm font-bold uppercase tracking-wide"><Trophy className="h-4 w-4" /> Get VIP Tips</Link>
          </div>

          {!loaded ? (
            <div className="mt-10 rounded-xl border border-[#2e2822] bg-[#1b1712] p-12 text-center text-neutral-400">Loading today's predictions…</div>
          ) : tips.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-[#2e2822] bg-[#1b1712] p-12 text-center">
              <Clock className="mx-auto h-10 w-10 text-orange-500" />
              <h3 className="mt-4 font-heading text-2xl font-extrabold text-white">Today's tips are being finalised</h3>
              <p className="mx-auto mt-2 max-w-md text-neutral-400">Our tipsters are completing their analysis. Check back shortly, or subscribe to receive every premium pick straight to your inbox.</p>
              <Link to="/pricing" className="btn-orange mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide">See VIP Plans <ArrowRight className="h-4 w-4" /></Link>
            </div>
          ) : rest.length === 0 ? (
            <div className="mt-8 rounded-xl border border-[#2e2822] bg-[#1b1712] p-8 text-center text-neutral-400">That's today's featured pick. More selections are added daily — subscribe to get every VIP tip.</div>
          ) : (
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((t) => (
                <TipCard key={t.id} tip={t} locked={t.tier === 'vip'} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Informational: betting markets explained (SEO) */}
      <section className="section-fade py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Beginner Friendly</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold text-white sm:text-4xl">Understanding Our Football Tips</h2>
            <p className="mx-auto mt-3 max-w-2xl text-neutral-300">New to betting? Here's a quick guide to the most common markets you'll see in our daily predictions.</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {markets.map((m, i) => (
              <div key={i} className="card-dark rounded-xl p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-500/15"><TrendingUp className="h-5 w-5 text-orange-500" /></div>
                <h3 className="mt-4 font-heading text-base font-bold text-white">{m.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{m.d}</p>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-neutral-500">
            All predictions are the opinion of our analysts and are provided for informational purposes only — they are not guarantees. 18+ only. Please gamble responsibly.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="card-dark relative overflow-hidden rounded-2xl p-10 text-center">
            <div className="absolute left-1/2 top-0 h-40 w-80 -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
            <ShieldCheck className="relative mx-auto h-12 w-12 text-orange-500" />
            <h2 className="relative mt-4 font-heading text-3xl font-extrabold text-white sm:text-4xl">Want Every Premium Pick?</h2>
            <p className="relative mx-auto mt-3 max-w-xl text-neutral-300">Unlock our full VIP football predictions, delivered daily before kickoff with complete statistical analysis.</p>
            <Link to="/pricing" className="btn-orange relative mt-8 inline-flex items-center gap-2 rounded-lg px-8 py-4 font-heading text-lg font-bold uppercase tracking-wide"><Trophy className="h-5 w-5" /> View VIP Plans</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
